import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Search, X, Truck as TruckIcon } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, StatusBadge, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BRAZIL_STATES } from '../utils/constants';
import { formatDocument, formatPhone } from '../utils/format';

const emptyForm = {
  person_type: 'PJ', name: '', corporate_name: '', trade_name: '',
  cpf: '', cnpj: '', ie: '',
  contacts: { phone: '', whatsapp: '', email: '' },
  address: { zip_code: '', street: '', number: '', neighborhood: '', city: '', state: '' },
  financial: { default_payment_method_id: '', default_payment_method_name: '', average_term_days: null, credit_limit: null },
  notes: '', status: 'active',
};

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [personType, setPersonType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (personType !== 'all') params.person_type = personType;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const { data } = await api.get('/suppliers', { params });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/masters/payment-methods', { params: { limit: 100 } })
      .then(r => setPaymentMethods(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [page, personType, statusFilter]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...emptyForm, ...s,
      contacts: { ...emptyForm.contacts, ...(s.contacts || {}) },
      address: { ...emptyForm.address, ...(s.address || {}) },
      financial: { ...emptyForm.financial, ...(s.financial || {}) },
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.financial.default_payment_method_id) {
        payload.financial.default_payment_method_name = paymentMethods.find(p => p.id === payload.financial.default_payment_method_id)?.name || '';
      }
      if (editing) { await api.put(`/suppliers/${editing.id}`, payload); toast.success('Fornecedor atualizado'); }
      else { await api.post('/suppliers', payload); toast.success('Fornecedor criado'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/suppliers/${deleteId}`); toast.success('Excluído'); setDeleteId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const docFor = (s) => s.person_type === 'PF' ? (s.cpf || '') : (s.cnpj || '');

  return (
    <div>
      <PageHeader title="Fornecedores" subtitle="Cadastro de fornecedores da empresa" onAction={openCreate} actionLabel="Novo Fornecedor" />

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, CNPJ, email..." className="pl-9" />
          </div>
          <Select value={personType} onValueChange={setPersonType}>
            <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="PF">Pessoa Física</SelectItem>
              <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativo</SelectItem>
              <SelectItem value="inactive">Inativo</SelectItem>
            </SelectContent>
          </Select>
          {(search || personType !== 'all' || statusFilter !== 'all') && (
            <Button variant="outline" onClick={() => { setSearch(''); setPersonType('all'); setStatusFilter('all'); }}><X className="h-4 w-4 mr-1" />Limpar</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium">Documento</th>
                <th className="text-left px-4 py-3 font-medium">Contato</th>
                <th className="text-left px-4 py-3 font-medium">Cidade/UF</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={6} className="p-4"><TableSkeleton rows={5} cols={6} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6}><EmptyState icon={TruckIcon} title="Nenhum fornecedor cadastrado" /></td></tr>
              ) : items.map((s) => (
                <tr key={s.id} className="tr-hover">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-neutral-500">{s.trade_name || s.corporate_name || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs">{formatDocument(docFor(s), s.person_type)}</span>
                    <div className="text-[10px] text-neutral-500">{s.person_type}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{formatPhone(s.contacts?.phone) || '-'}</div>
                    <div className="text-[11px] text-neutral-500">{s.contacts?.email || ''}</div>
                  </td>
                  <td className="px-4 py-3">{s.address?.city ? `${s.address.city}/${s.address?.state || '-'}` : '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status === 'active' ? 'Ativo' : 'Inativo'} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(s.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
            <DialogDescription>Dados organizados por aba.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Tabs defaultValue="geral">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="geral">Geral</TabsTrigger>
                <TabsTrigger value="contato">Contato</TabsTrigger>
                <TabsTrigger value="endereco">Endereço</TabsTrigger>
                <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
              </TabsList>
              <TabsContent value="geral" className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1.5">
                  <Label>Tipo Pessoa *</Label>
                  <Select value={form.person_type} onValueChange={(v) => setForm({ ...form, person_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="PF">Pessoa Física</SelectItem><SelectItem value="PJ">Pessoa Jurídica</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status *</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Ativo</SelectItem><SelectItem value="inactive">Inativo</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>{form.person_type === 'PF' ? 'Nome *' : 'Nome / Razão Social *'}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                {form.person_type === 'PJ' && <>
                  <div className="space-y-1.5"><Label>Razão Social</Label><Input value={form.corporate_name || ''} onChange={(e) => setForm({ ...form, corporate_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Nome Fantasia</Label><Input value={form.trade_name || ''} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>CNPJ</Label><Input value={form.cnpj || ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Inscrição Estadual</Label><Input value={form.ie || ''} onChange={(e) => setForm({ ...form, ie: e.target.value })} /></div>
                </>}
                {form.person_type === 'PF' && <div className="space-y-1.5 md:col-span-2"><Label>CPF</Label><Input value={form.cpf || ''} onChange={(e) => setForm({ ...form, cpf: e.target.value })} /></div>}
                <div className="space-y-1.5 md:col-span-2"><Label>Observações</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </TabsContent>
              <TabsContent value="contato" className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1.5"><Label>Telefone</Label><Input value={form.contacts.phone} onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, phone: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.contacts.whatsapp} onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, whatsapp: e.target.value } })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Email</Label><Input type="email" value={form.contacts.email} onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, email: e.target.value } })} /></div>
              </TabsContent>
              <TabsContent value="endereco" className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                <div className="space-y-1.5"><Label>CEP</Label><Input value={form.address.zip_code} onChange={(e) => setForm({ ...form, address: { ...form.address, zip_code: e.target.value } })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Rua</Label><Input value={form.address.street} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label>Número</Label><Input value={form.address.number} onChange={(e) => setForm({ ...form, address: { ...form.address, number: e.target.value } })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Bairro</Label><Input value={form.address.neighborhood} onChange={(e) => setForm({ ...form, address: { ...form.address, neighborhood: e.target.value } })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Cidade</Label><Input value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label>UF</Label>
                  <Select value={form.address.state} onValueChange={(v) => setForm({ ...form, address: { ...form.address, state: v } })}>
                    <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{BRAZIL_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </TabsContent>
              <TabsContent value="financeiro" className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1.5">
                  <Label>Forma de pagamento padrão</Label>
                  <Select value={form.financial.default_payment_method_id || 'none'} onValueChange={(v) => setForm({ ...form, financial: { ...form.financial, default_payment_method_id: v === 'none' ? '' : v } })}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {paymentMethods.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Prazo médio (dias)</Label><Input type="number" value={form.financial.average_term_days ?? ''} onChange={(e) => setForm({ ...form, financial: { ...form.financial, average_term_days: e.target.value === '' ? null : Number(e.target.value) } })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Limite de crédito (R$)</Label><Input type="number" step="0.01" value={form.financial.credit_limit ?? ''} onChange={(e) => setForm({ ...form, financial: { ...form.financial, credit_limit: e.target.value === '' ? null : Number(e.target.value) } })} /></div>
              </TabsContent>
            </Tabs>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir fornecedor?</AlertDialogTitle>
            <AlertDialogDescription>Soft delete.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
